import React, { useState, useEffect, useRef } from 'react';
import { calendarData } from '../data/calendarData';
import { getThemeForMonth, birthdayTheme } from '../themes/seasonalThemes';
import Chihuahua from './ChihuahuaElement';

interface CalendarPageProps {
  initialDate?: string;
}

const CalendarPage: React.FC<CalendarPageProps> = ({ initialDate }) => {
  // Get today's date in YYYY-MM-DD format
  const today = new Date();
  const formattedToday = initialDate || `${today.getFullYear()}-${(today.getMonth() + 1).toString().padStart(2, '0')}-${today.getDate().toString().padStart(2, '0')}`;
  const [selectedDate, setSelectedDate] = useState<string>(formattedToday);
  const [isSupremeBlessingPlaying, setIsSupremeBlessingPlaying] = useState(false);
  const [isBgSongPlaying, setIsBgSongPlaying] = useState(false);
  const [bgImageLoaded, setBgImageLoaded] = useState(false);
  const birthdayAudioRef = useRef<HTMLAudioElement>(null);
  const bgSongRef = useRef<HTMLAudioElement>(null);

  // Get today's data
  const todayData = calendarData.find(day => day.date === selectedDate) || calendarData[0];

  // Extract month from selected date
  const month = parseInt(selectedDate.split('-')[1]);

  // Get appropriate theme
  const isBirthday = selectedDate === '2026-05-21';
  const theme = isBirthday ? birthdayTheme : getThemeForMonth(month);
  void theme; // used for future theme customization

  // 点击播放至尊祝福
  const playSupremeBlessing = () => {
    if (birthdayAudioRef.current) {
      if (isSupremeBlessingPlaying) {
        birthdayAudioRef.current.pause();
        setIsSupremeBlessingPlaying(false);
      } else {
        birthdayAudioRef.current.currentTime = 0;
        birthdayAudioRef.current.play()
          .then(() => {
            setIsSupremeBlessingPlaying(true);
          })
          .catch((error) => {
            console.error('Error playing supreme blessing:', error);
            alert('无法播放生日祝福，请检查浏览器设置');
          });
      }
    }
  };

  // 5月21日自动播放生日快乐歌（背景音乐）
  useEffect(() => {
    if (selectedDate === '2026-05-21') {
      const timer = setTimeout(() => {
        if (bgSongRef.current) {
          bgSongRef.current.play()
            .then(() => {
              setIsBgSongPlaying(true);
            })
            .catch(() => {
              // 自动播放被浏览器阻止，正常情况
              setIsBgSongPlaying(false);
            });
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [selectedDate]);

  // 切换生日快乐歌播放/暂停
  const toggleBgSong = () => {
    if (bgSongRef.current) {
      if (isBgSongPlaying) {
        bgSongRef.current.pause();
        setIsBgSongPlaying(false);
      } else {
        bgSongRef.current.play()
          .then(() => setIsBgSongPlaying(true))
          .catch(() => {});
      }
    }
  };

  // 使用一个静态图片URL作为后备
  const staticImageUrl = "https://picsum.photos/seed/default/1200/800";
  
  void staticImageUrl; // fallback image, used on error

  // 确保 todayData.photo 存在，如果不存在则使用 getPhotoForDate 获取
  const photoUrl = todayData.photo || `https://picsum.photos/seed/default-${selectedDate}/1200/800`;
  
  // 当日期变化时重置加载状态
  useEffect(() => {
    setBgImageLoaded(false);
  }, [selectedDate]);

  return (
    <div
      style={{
        position: 'relative',
        minHeight: '100vh',
        backgroundImage: `url(${photoUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        padding: '20px'
      }}
    >
      {/* Loading overlay */}
      {!bgImageLoaded && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(240, 240, 240, 0.8)', // Light gray semi-transparent background while loading
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div style={{ fontSize: '18px', color: '#666' }}>加载中...</div>
        </div>
      )}
      
      {/* 预加载背景图片 */}
      <img
        src={photoUrl}
        alt=""
        style={{ display: 'none' }}
        onLoad={() => setBgImageLoaded(true)}
        onError={() => {
          // 如果加载失败，使用默认图片
          setBgImageLoaded(true);
        }}
      />
      
      {/* Background overlay for readability */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(255, 255, 255, 0.2)', // Reduced opacity for better visibility
          zIndex: 2
        }}
      ></div>

      {/* 吉娃娃二妞 */}
      <Chihuahua
        position="top-left"
        size={80}
        opacity={0.85}
        variant={Math.random() < 0.5 ? 0 : 1}
      />

      {/* Content container */}
      <div style={{ position: 'relative', zIndex: 10, padding: '20px' }}>
        {/* 5月21日生日功能 */}
        {isBirthday && (
          <>
            {/* 右上角：生日快乐歌播放/暂停 + 至尊祝福 */}
            <div
              style={{
                position: 'fixed',
                top: '65px',
                right: '20px',
                zIndex: 200,
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                alignItems: 'center',
              }}
            >
              {/* 生日快乐歌 播放/暂停 */}
              <button
                onClick={toggleBgSong}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  border: '1px solid rgba(255, 255, 255, 0.4)',
                  borderRadius: '50%',
                  width: '60px',
                  height: '60px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px',
                  color: '#fff',
                  cursor: 'pointer',
                  backdropFilter: 'blur(5px)',
                  transition: 'transform 0.2s ease',
                }}
              >
                {isBgSongPlaying ? '⏸️' : '🎵'}
              </button>
              <div
                style={{
                  marginTop: '-8px',
                  fontSize: '13px',
                  color: '#fff',
                  fontFamily: '"KaiTi", "楷体", serif',
                  textShadow: '1px 1px 3px rgba(0,0,0,0.3)',
                  whiteSpace: 'nowrap',
                }}
              >
                生日快乐歌
              </div>

              {/* 至尊祝福 */}
              <button
                onClick={playSupremeBlessing}
                style={{
                  backgroundColor: 'rgba(212, 175, 55, 0.9)',
                  border: '2px solid rgba(255, 215, 0, 0.8)',
                  borderRadius: '50%',
                  width: '70px',
                  height: '70px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '32px',
                  color: '#fff',
                  cursor: 'pointer',
                  boxShadow: '0 4px 16px rgba(212, 175, 55, 0.4)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.1)';
                  e.currentTarget.style.boxShadow = '0 6px 24px rgba(212, 175, 55, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(212, 175, 55, 0.4)';
                }}
              >
                
              </button>
              <div
                style={{
                  marginTop: '-4px',
                  fontSize: '18px',
                  color: '#fff',
                  fontFamily: '"LiSu", "隶书", "KaiTi", "楷体", serif',
                  fontWeight: 'bold',
                  textShadow: '1px 1px 3px rgba(0,0,0,0.3)',
                  whiteSpace: 'nowrap',
                }}
              >
                至尊祝福
              </div>
            </div>

            {/* 生日快乐歌 背景音频 */}
            <audio ref={bgSongRef} loop onEnded={() => setIsBgSongPlaying(false)}>
              <source src="/生日快乐歌.mp3" type="audio/mpeg" />
              您的浏览器不支持音频播放。
            </audio>

            {/* 生日祝福音频（隐藏） */}
            <audio ref={birthdayAudioRef} onEnded={() => setIsSupremeBlessingPlaying(false)}>
              <source src="/生日祝福.mp3" type="audio/mpeg" />
              您的浏览器不支持音频播放。
            </audio>
          </>
        )}

        {/* Calendar header */}
        <div className="text-center mb-4">
          <h1 className="display-4" style={{
            color: '#000000',
            textShadow: '1px 1px 2px rgba(0,0,0,0.1)',
            fontFamily: '"KaiTi", "楷体", serif'
          }}>
            poem的日历
          </h1>
          <p className="lead" style={{ color: '#555555', fontFamily: '"KaiTi", "楷体", serif' }}>
            每日温馨祝福，诗意生活
          </p>
        </div>

        {/* Calendar navigation */}
        <div className="row justify-content-center mb-4">
          <div className="col-md-8">
            <div className="d-flex justify-content-between align-items-center">
              <button
                className="btn"
                onClick={() => {
                  const prevDate = new Date(selectedDate);
                  prevDate.setDate(prevDate.getDate() - 1);
                  setSelectedDate(prevDate.toISOString().split('T')[0]);
                }}
                style={{
                  backgroundColor: 'rgba(85, 85, 85, 0.85)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 20px',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  backdropFilter: 'blur(5px)'
                }}
              >
                ← Previous
              </button>

              <h2
                className="text-center"
                style={{
                  color: '#000000',
                  fontSize: '1.8rem',
                  fontFamily: '"KaiTi", "楷体", serif'
                }}
              >
                {new Date(selectedDate).toLocaleDateString('zh-CN', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </h2>

              <button
                className="btn"
                onClick={() => {
                  const nextDate = new Date(selectedDate);
                  nextDate.setDate(nextDate.getDate() + 1);
                  setSelectedDate(nextDate.toISOString().split('T')[0]);
                }}
                style={{
                  backgroundColor: 'rgba(85, 85, 85, 0.85)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 20px',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  backdropFilter: 'blur(5px)'
                }}
              >
                Next →
              </button>
            </div>
          </div>
        </div>

        {/* Holiday indicator */}
        {todayData.isHoliday && (
          <div className="row justify-content-center mb-3">
            <div className="col-md-8">
              <div
                className="alert text-center"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  border: '2px dashed #555555',
                  borderRadius: '10px',
                  color: '#000000',
                  fontFamily: '"KaiTi", "楷体", serif'
                }}
              >
                🎉 今天是 <strong>{todayData.holidayName}</strong>！
              </div>
            </div>
          </div>
        )}

        {/* Main calendar card */}
        <div className="row justify-content-center">
          <div className="col-md-8">
            <div
              className="card shadow-lg"
              style={{
                borderRadius: '15px',
                overflow: 'hidden',
                backgroundColor: 'rgba(255, 255, 255, 0.85)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.5)'
              }}
            >
              <div className="row no-gutters">
                {/* Left side: Themed Photo related to the blessing */}
                <div className="col-md-6">
                  <div
                    className="position-relative"
                    style={{
                      height: '400px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '16px 16px 16px 32px',
                      overflow: 'hidden',
                    }}
                  >
                    {/* Photo container: scaled to 4/5, centered, rounded corners */}
                    <div
                      style={{
                        height: '320px',
                        width: '100%',
                        borderRadius: '15px',
                        overflow: 'hidden',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      }}
                    >
                      <div
                        style={{
                          height: '100%',
                          backgroundImage: `url(${photoUrl})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                        }}
                      >
                        {/* Overlay for softening the image */}
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backgroundColor: 'rgba(255, 255, 255, 0.3)',
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right side: Content */}
                <div className="col-md-6">
                  <div className="card-body p-5 d-flex flex-column">
                    <div className="flex-grow-1">
                      <h3
                        className="card-title mb-4"
                        style={{
                          color: isBirthday ? '#2E7D32' : '#000000',
                          fontSize: '1.8rem',
                          fontWeight: isBirthday ? 'bold' : 'normal',
                          fontFamily: '"LiSu", "隶书", "KaiTi", "楷体", serif'
                        }}
                      >
                        {isBirthday ? '🎂 生日快乐! 🎂' : todayData.blessing}
                      </h3>

                      <div
                        className="mb-4 p-3 rounded"
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.7)',
                          borderLeft: '4px solid #555555',
                          fontFamily: '"KaiTi", "楷体", serif'
                        }}
                      >
                        <p
                          className="mb-0"
                          style={{
                            fontStyle: 'italic',
                            color: '#000000',
                            fontSize: '1.1rem',
                            fontFamily: '"KaiTi", "楷体", serif'
                          }}
                        >
                          "{todayData.poem}"
                        </p>
                      </div>

                      <div className="mt-4" style={{ fontFamily: '"KaiTi", "楷体", serif' }}>
                        <h5 style={{ color: '#555555', fontFamily: '"LiSu", "隶书", "KaiTi", "楷体", serif' }}>今日寄语</h5>
                        <p style={{ color: '#000000', fontFamily: '"KaiTi", "楷体", serif' }}>
                          {todayData.dailyMessage || "愿你的每一天都充满阳光与温暖，心境如诗如画。"}
                        </p>
                      </div>
                    </div>

                    <div
                      className="d-flex justify-content-between align-items-center mt-auto pt-3 border-top"
                      style={{
                        borderColor: '#55555540',
                        fontFamily: '"KaiTi", "楷体", serif'
                      }}
                    >
                      <small style={{ color: '#000000', fontFamily: '"KaiTi", "楷体", serif' }}>
                        {selectedDate}
                      </small>
                      <div className="d-flex">
                        <span className="mx-1">📅</span>
                        <span className="mx-1">❤️</span>
                        <span className="mx-1">✨</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative Chinese elements */}
        <div className="text-center mt-4" style={{ color: '#55555580', fontFamily: '"KaiTi", "楷体", serif' }}>
          <p style={{ fontFamily: '"LiSu", "隶书", "KaiTi", "楷体", serif' }}>让诗意融入生活，让温暖伴随每日</p>
        </div>
      </div>
    </div>
  );
};

export default CalendarPage;